import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap, uploadFile } from "../client";
import type { components } from "../generated/schema";
import { noErrorToast } from "../mutationMeta";
import type { TenantSettings } from "../types";
import { sessionKey } from "./auth";

export const tenantKeys = {
  context: (slug: string) => ["tenant", slug, "context"] as const,
  settings: (slug: string) => ["tenant", slug, "settings"] as const,
};

export function useTenantContext(slug: string) {
  return useQuery({
    queryKey: tenantKeys.context(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/context/", { params: { path: { tenant_slug: slug } } })),
    retry: false,
    staleTime: 30_000,
  });
}

export function useTenantSettings(slug: string) {
  return useQuery({
    queryKey: tenantKeys.settings(slug),
    queryFn: () => unwrap(api.GET("/api/t/{tenant_slug}/settings/", { params: { path: { tenant_slug: slug } } })),
  });
}

type SettingsPatch = components["schemas"]["PatchedTenantSettingsUpdateRequest"];

export function useUpdateTenantSettings(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: SettingsPatch) =>
      unwrap(api.PATCH("/api/t/{tenant_slug}/settings/", { params: { path: { tenant_slug: slug } }, body })),
    onSuccess: (settings) => {
      qc.setQueryData(tenantKeys.settings(slug), settings);
      qc.invalidateQueries({ queryKey: tenantKeys.context(slug) });
      qc.invalidateQueries({ queryKey: sessionKey });
    },
  });
}

export function useTenantLogo(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (file: File | null) =>
      uploadFile<TenantSettings>(`/api/t/${slug}/settings/logo/`, file, file ? "POST" : "DELETE"),
    onSuccess: (settings) => {
      qc.setQueryData(tenantKeys.settings(slug), settings);
      qc.invalidateQueries({ queryKey: tenantKeys.context(slug) });
      qc.invalidateQueries({ queryKey: sessionKey });
    },
  });
}
